import type { ReactNode } from 'react';
import { Alert, Box, Stack } from '@mui/material';
import type { StaffingChangeSummary } from '../operations/model';
import {
  formatDateOnly,
  formatInstantDate,
  processLabel,
  progressDescription,
  progressTone,
  statusPresentation,
  subjectLabel,
} from '../model/staffingPagePresentation';
import {
  DetailDrawer,
  DetailDrawerBody,
  DefinitionList,
  SectionCard,
  ProgressStep,
  StatusBadge,
  ValueBadge,
} from '../../../design-system';
import { ChangeComparison } from './ChangeComparison';

export type StaffingChangeDetailDrawerProps = {
  open: boolean;
  change: StaffingChangeSummary;
  today: string;
  footer?: ReactNode;
  onClose: () => void;
};

export function StaffingChangeDetailDrawer({
  open,
  change,
  today,
  footer,
  onClose,
}: StaffingChangeDetailDrawerProps) {
  const presentation = statusPresentation(change, today);

  return <DetailDrawer
    open={open}
    onClose={onClose}
    title={subjectLabel(change)}
    meta={change.descriptor}
    status={<StatusBadge label={presentation.label} tone={presentation.tone} />}
    footer={footer}
  >
    <DetailDrawerBody>
      <Stack spacing={2}>
        <SectionCard title="기본 정보" showDivider={false}>
          <Box sx={{ px: 2, pb: 2 }}>
            <DefinitionList items={[
              ['발령 유형', <ValueBadge label={processLabel(change.processType)} />],
              ['시행일', formatDateOnly(change.effectiveDate)],
              ['대상', subjectLabel(change)],
              ['작성일', formatInstantDate(change.createdAt)],
              ['요청일', formatInstantDate(change.submittedAt)],
              ['정정 원본', change.correctionOf?.descriptor || '원본 변경 건'],
            ]} />
          </Box>
        </SectionCard>
        <SectionCard title="발령 진행" showDivider={false}>
          <Box sx={{ px: 2, pb: 2 }}>
            <ProgressStep marker="1" label="초안" tone={progressTone(change, 'DRAFT', today)} description={progressDescription(change, 'DRAFT', today)} />
            <ProgressStep marker="2" label="승인" tone={progressTone(change, 'APPROVAL', today)} description={progressDescription(change, 'APPROVAL', today)} />
            <ProgressStep marker="3" label="시행" tone={progressTone(change, 'EFFECTIVE', today)} description={progressDescription(change, 'EFFECTIVE', today)} />
          </Box>
        </SectionCard>
        <SectionCard title="변경 내용" showDivider={false}>
          <Box sx={{ px: 2, pb: 2 }}>
            {change.changes?.length ? <ChangeComparison layout="inline" changes={change.changes} /> : <Alert severity="info">변경 전후 상세는 원장 스냅샷이 연결되면 표시됩니다.</Alert>}
          </Box>
        </SectionCard>
      </Stack>
    </DetailDrawerBody>
  </DetailDrawer>;
}
